import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'

import styles from '../OpportunitiesTable.module.scss'
import { useVisibilityFilter } from '../../hooks/useVisibilityFilter'

import { Checkbox, Filter, FilterList, SearchInput, TableSearchAndFilters } from '@percent/lemonade'
import { SearchAndFiltersProps } from './SearchAndFilters.types'
import { OpportunityStatus } from '@percent/partner-dashboard/services/workplaceGiving/workplaceGivingService.types'
import { useSearchFilter } from '@percent/partner-dashboard/common/hooks/useSearchFilter/useSearchFilter'
import { POSTHOG_FEATURE_FLAGS, usePostHogFeatureFlag } from '@percent/partner-dashboard/common/hooks'

export function SearchAndFilters({ queryParams, setQueryParams }: Readonly<SearchAndFiltersProps>) {
  const { t } = useTranslation()
  const isDraftsEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.PD_OPPORTUNITY_DRAFTS)

  const allStatuses = isDraftsEnabled
    ? [OpportunityStatus.DRAFT, OpportunityStatus.ACTIVE, OpportunityStatus.CLOSED, OpportunityStatus.CANCELLED]
    : [OpportunityStatus.ACTIVE, OpportunityStatus.CLOSED, OpportunityStatus.CANCELLED]

  const { visibilityOptions, selectedVisibility, onVisibilityChange } = useVisibilityFilter({
    queryParams,
    setQueryParams
  })

  const { searchValue, handleChange, handleKeyPress, handleClearValue } = useSearchFilter({
    queryParams: { query: queryParams?.query },
    setQueryParams: params =>
      setQueryParams?.({
        query: params.query
      })
  })

  const statusesOptions = useMemo(
    () => [
      { label: t('status.all'), value: '' },
      ...(isDraftsEnabled ? [{ label: t('status.draft'), value: OpportunityStatus.DRAFT }] : []),
      { label: t('status.active'), value: OpportunityStatus.ACTIVE },
      { label: t('status.past'), value: OpportunityStatus.CLOSED },
      { label: t('status.cancelled'), value: OpportunityStatus.CANCELLED }
    ],
    [t, isDraftsEnabled]
  )

  const selectedStatus: OpportunityStatus | '' = useMemo(() => {
    if (queryParams?.status?.length === allStatuses.length) {
      return ''
    }

    return queryParams?.status?.[0] ?? ''
  }, [queryParams?.status, allStatuses.length])

  const handleStatusFilterChange = filterStatus => {
    if (!filterStatus?.length) {
      setQueryParams?.({
        status: allStatuses
      })
    } else {
      setQueryParams?.({
        status: [filterStatus]
      })
    }
  }

  const handleAvailableOnlyChange = (checked: boolean) => {
    setQueryParams?.({
      ...queryParams,
      availableOnly: checked || undefined
    })
  }

  return (
    <TableSearchAndFilters>
      <div className={styles.searchAndFilters}>
        <SearchInput
          name="search"
          value={searchValue}
          onKeyDown={handleKeyPress}
          onChange={handleChange}
          handleClearValue={handleClearValue}
          placeholder={t('table.volunteeringSearchPlaceholder')}
          status={searchValue.length >= 255 ? 'danger' : 'default'}
          statusMessage={t('errorMessage.shouldNotExceed255')}
        />
        <Filter
          label={t('table.header.status')}
          options={statusesOptions}
          selectedValue={selectedStatus}
          onChange={handleStatusFilterChange}
          testId="opportunity-status-filter"
        />
        <Checkbox
          name="availableOnly"
          label={t('typography.availableOnly')}
          variant="default"
          active={queryParams?.availableOnly || false}
          onChange={e => handleAvailableOnlyChange(e.currentTarget.checked)}
          dataTestId="available-only-checkbox"
          labelWrap="nowrap"
        />
        <FilterList
          label={t('table.visibility')}
          options={visibilityOptions}
          selectedValues={selectedVisibility}
          onChange={onVisibilityChange}
          testId="opportunity-visibility"
        />
      </div>
    </TableSearchAndFilters>
  )
}
